/**
 * @module cercaliagl/ContextMenu
 */
import maplibregl from 'maplibre-gl';
import { assert } from './AssertionError';
import LonLat from './LonLat';
import { i18n } from './dictionary';
import VContextMenu from './view/VContextMenu';
/**
 * @typedef {Object} ContextMenuOptions
 * @property {string|undefined} id Id option
 * @property {import('./Map').default|undefined} map Map
 * @property {LonLat|undefined} fixedPosition Fixed position in map
 * @property {string} className Context menu class name
 */
/**
 * @typedef {Object} ContextMenuSubmenu
 * @property {string|undefined} id Id submenu
 * @property {string|undefined} subMenu Sub menu
 * @property {string|undefined} label  Label
 */
/**
 * @typedef {Object} ContextMenuSubmenuOption
 * @property {string|undefined} id Id
 * @property {string|undefined} label Label
 * @property {Function|undefined} callback Click callback function.
 */
/**
 * @enum {string}
 */
export var OptionId = {
    /**
     * @api
     */
    ADDRESS: 'getaddress',
    /**
     * @api
     */
    ROUTING: 'getrouting',
    /**
     * @api
     */
    DISTANCE: 'getdistance',
};
/**
 * @type {Object.<string, string>}
 * @api
 */
export var Option = {
    /**
     * @api
     */
    ADDRESS: 'Address',
    /**
     * @api
     */
    ROUTING: 'Routing',
    /**
     * @api
     */
    DISTANCE: 'Distance',
};
/**
 * @enum {string}
 */
export var SubmenuRoutingId = {
    /**
     * @api
     */
    ROUTING_START: 'routingstart',
    /**
     * @api
     */
    ROUTING_STEP: 'routingstep',
    /**
     * @api
     */
    ROUTING_END: 'routingend',
};
/**
 * @enum {string}
 */
export var SubmenuRouting = {
    /**
     * @api
     */
    ROUTING_START: 'From here',
    /**
     * @api
     */
    ROUTING_STEP: 'Waypoint here',
    /**
     * @api
     */
    ROUTING_END: 'Destination here',
};
/**
 * @enum {string}
 */
export var SubmenuDistanceId = {
    /**
     * @api
     */
    LINE: 'line',
    /**
     * @api
     */
    ROAD: 'road',
    /**
     * @api
     */
    AREA: 'area',
};
/**
 * @enum {string}
 */
export var SubmenuDistance = {
    /**
     * @api
     */
    LINE: 'Line',
    /**
     * @api
     */
    ROAD: 'Road',
    /**
     * @api
     */
    AREA: 'Area',
};
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const contextMenu = new cercaliagl.ContextMenu.({ ... });
 * ```
 *
 * Class for creating a context menu. This control permits: <br/>
 *
 * <ul>
 *   <li>- Draw a menu in a {@link import("./LonLat").default LonLat} position</li>
 *   <li>- Add / Remove options in menu.</li>
 *   <li>- Enable / Disable options of the menu.</li>
 *   <li>- Show / Hide options of the menu.</li>
 * </ul>
 * <br/>
 * <br/>
 *
 * Example creating a custom contexte menu (without default options):
 *
 * ```js
 * const map = new cercaliagl.Map({
 *   controls: [],
 *   rightClickMenuOptions: [],
 * });
 * const contextMenu = new cercaliagl.ContextMenu({ map });
 * contextMenu.addOption('drawMarker', 'Create marker here', (position) => {
 *   const marker = new cercaliagl.Marker({
 *     position,
 *     simpleLabel: new cercaliagl.SimpleLabel({ text: position.toString() }),
 *   });
 *   map.addMarker(marker);
 * });
 * ```
 *
 * @api
 */
var ContextMenu = /** @class */ (function () {
    /**
     * @constructor
     * @param {ContextMenuOptions} [options] Options.
     */
    function ContextMenu(options) {
        if (options === void 0) { options = {}; }
        var _a;
        var contextMenuView = new VContextMenu();
        /**
         * @private
         * @type {VContextMenu}
         */
        this.contextMenuView_ = contextMenuView;
        /**
         * @private
         * @type {string}
         */
        this.className_ = (_a = options.className) !== null && _a !== void 0 ? _a : '';
        /**
         * @private
         * @type {HTMLElement}
         */
        this.element_ = contextMenuView.getElement();
        /**
         * @private
         * @type {import('./Map').default}
         */
        this.map_ = options.map ? options.map : null;
        /**
         * @private
         * @type {LonLat|undefined}
         */
        this.fixedPosition_ = options.fixedPosition !== undefined ? options.fixedPosition : undefined;
        /**
         * @private
         * @type {maplibregl.Popup}
         */
        this.contextMenuOverlay_ = null;
        /**
         * @private
         * @type {maplibregl.LngLat|null}
         */
        this.currentPosition_ = null;
        /**
         * @private
         * @type {boolean}
         */
        this.visible_ = false;
        this.initialize_();
    }
    /**
     * Initalize the context menu.
     * @private
     */
    ContextMenu.prototype.initialize_ = function () {
        this.element_.setAttribute('id', this.id_);
        this.contextMenuOverlay_ = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: "context-menu-popup".concat(this.className_ ? " ".concat(this.className_) : ''),
        });
        this.contextMenuOverlay_.setDOMContent(this.element_);
        var mbMap = this.map_.getMapboxglMap();
        if (this.map_ !== null) {
            this.contextMenuOverlay_.remove();
            this.map_.addContextMenu(this);
        }
        this.contextMenuOverlay_.addTo(mbMap);
    };
    /**
     * Returns `true` if the context menu is visible.
     * @return {boolean} Returns `true` if is visible.
     * @api
     */
    ContextMenu.prototype.isVisible = function () {
        return this.visible_;
    };
    /**
     * Add a new option in the context menu at the end or in a specific position. Returns options identifier.
     * If the id options exist removes previous option.
     * @param {!string} id Option's identifier.
     * @param {!string} name Text will be displayed in option.
     * @param {!Function} callback Function will be fired when users click the option.
     * @param {number} [position] The position where option will be added.
     * @param {string} [className] Classname for option.
     * @api
     */
    ContextMenu.prototype.addOption = function (id, name, callbackFn, position, className) {
        var _this = this;
        if (this.existOption(id)) {
            this.removeOption(id);
        }
        var newListElement = /** @type {import('./view/VContextMenu.js').MenuItem} */ ({
            id: id,
            label: name,
            className: className,
        });
        this.contextMenuView_.addOption(newListElement, position);
        this.contextMenuView_.on("on-".concat(id), function () { return callbackFn(LonLat.fromMapboxgl(_this.currentPosition_)); });
    };
    /**
     * Add submenu. A submenu contains one or more options
     * @param {string} id Specify internal Id element
     * @param {string} name Label of submenu.
     * @param {Array.<import('./ContextMenu').ContextMenuSubmenuOption>} optionList Option list. Example: `{[{id:'btn1', label:'Button1', callback: 'callbackFn1'},
     *    {id:'btn2', label:'Button2', callback: 'callbackFn1'}]}`.
     * @api
     */
    ContextMenu.prototype.addSubMenu = function (id, name, optionList) {
        var _this = this;
        var newListElement = {
            id: id,
            label: name,
            subMenu: optionList,
        };
        this.contextMenuView_.addOption(newListElement);
        var _loop_1 = function (i) {
            var sufixEventId = optionList[i].id;
            var callbackFn = optionList[i].callback;
            this_1.contextMenuView_.on("on-".concat(sufixEventId), function () { return callbackFn(LonLat.fromMapboxgl(_this.currentPosition_)); });
        };
        var this_1 = this;
        for (var i = 0; i < optionList.length; i += 1) {
            _loop_1(i);
        }
    };
    /**
     * Close the menu.
     * @api
     */
    ContextMenu.prototype.close = function () {
        this.contextMenuView_.hide();
        this.visible_ = false;
    };
    /**
     * Returns `true` if the option exists.
     * @param {string} id Option's identifier.
     * @return {boolean} True if exists.
     * @api
     */
    ContextMenu.prototype.existOption = function (id) {
        return this.contextMenuView_.exists(id);
    };
    /**
     * Open the menu. If menu isn't position fixed needs a position to be displayed.
     * @param {import("maplibre-gl").LngLat|LonLat} lonLat If menu isn't position fixed, menu will be displayed in ths position.
     * @api
     */
    ContextMenu.prototype.open = function (lonLat) {
        assert(this.map_ !== null, i18n('assertMapNotDefined'));
        var normalizedLonLat = lonLat instanceof LonLat ? lonLat.toMapboxLngLat() : lonLat;
        this.currentPosition_ = this.fixedPosition_ ? this.fixedPosition_ : normalizedLonLat;
        this.contextMenuOverlay_.setLngLat(this.currentPosition_);
        this.visible_ = true;
        this.contextMenuView_.reset(); // Close collapse & reset bolds
        this.contextMenuView_.show();
    };
    /**
     * Remove all options in context menu.
     * @api
     */
    ContextMenu.prototype.removeAllOptions = function () {
        this.contextMenuView_.removeAllOptions();
    };
    /**
     * Remove an option from the context menu.
     * @param {string} id Option's identifier.
     * @api
     */
    ContextMenu.prototype.removeOption = function (id) {
        this.contextMenuView_.removeOption(id);
    };
    /**
     * Show menu option.
     * @param {string} id Id.
     * @api
     */
    ContextMenu.prototype.showOption = function (id) {
        this.contextMenuView_.showOption(id);
    };
    /**
     * Hide menu option.
     * @param {string} id Id.
     * @api
     */
    ContextMenu.prototype.hideOption = function (id) {
        this.contextMenuView_.hideOption(id);
    };
    /**
     * Open Submenu
     * @param {string} id Id.
     * @api
     */
    ContextMenu.prototype.openSubmenu = function (id) {
        this.contextMenuView_.toggleSubmenu(id, true);
    };
    /**
     * Close Submenu
     * @param {string} id Id.
     * @api
     */
    ContextMenu.prototype.closeSubmenu = function (id) {
        this.contextMenuView_.toggleSubmenu(id, false);
    };
    return ContextMenu;
}());
export default ContextMenu;
//# sourceMappingURL=ContextMenu.js.map